import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API, getPhotoUrl } from "../App";
import { Button } from "../components/ui/button";
import { Check, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import IntentionModal from "../components/IntentionModal";

const ProfileView = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIntentionModal, setShowIntentionModal] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSendInterest = async () => {
    try {
      await axios.post(`${API}/interests/send`, { to_user_id: userId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('browse.interestSentSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error sending interest");
    }
  };

  const handleInitiateChat = async () => {
    try {
      const response = await axios.post(`${API}/chat/initiate`, { to_user_id: userId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/chat/${response.data.chat_id}`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('profile.paymentRequired'));
        navigate("/pricing");
      } else {
        toast.error(error.response?.data?.detail || "Error initiating chat");
      }
    }
  };

  const hasActiveSubscription = user?.subscription && user?.subscription_expires && 
    new Date(user.subscription_expires) > new Date();

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rose-50 flex items-center justify-center">
        <div className="animate-pulse text-rose-500 font-serif text-2xl">{t('common.loading')}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-rose-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-rose-900 mb-4">{t('profile.notFound')}</h2>
          <Button onClick={() => navigate("/browse")} className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full">
            {t('profile.backToBrowse')}
          </Button>
        </div>
      </div>
    );
  }

  const age = calculateAge(profile.date_of_birth);

  return (
    <div className="min-h-screen bg-rose-50">
      {/* Header */}
      <div className="bg-white border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-rose-600 hover:text-rose-800 transition-colors"
              data-testid="back-btn"
            >
              ← {t('common.back')}
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/images/singleseen_logo_transparent.png" alt="SingleSeen" className="h-12 w-auto" />
            </Link>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Photo & Basic Info */}
        <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden mb-6" data-testid="profile-header">
          <div className="relative h-80 md:h-96 bg-rose-200">
            {profile.photos?.[0] ? (
              <>
                <img 
                  src={getPhotoUrl(profile.photos[0])}
                  alt={profile.first_name}
                  className={`w-full h-full object-cover ${profile.photos_blurred ? 'photo-blur' : ''}`}
                />
                {profile.photos_blurred && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/30 backdrop-blur-md">
                    <div className="bg-white rounded-2xl px-8 py-6 text-center shadow-lg max-w-sm">
                      <span className="text-4xl text-rose-500 block mb-3">🔒</span>
                      <h3 className="font-serif text-xl text-rose-900 mb-2">{t('profile.photoProtected')}</h3>
                      <p className="text-rose-600 text-sm mb-4">{t('profile.subscribeToViewPhotos')}</p>
                      <Button 
                        onClick={() => navigate("/pricing")}
                        className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
                        data-testid="subscribe-photo-btn"
                      >
                        {t('common.viewPlans')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-rose-100">
                <span className="text-6xl font-serif text-rose-300">{profile.first_name?.[0] || "?"}</span>
              </div>
            )}
          </div>

          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="font-serif text-3xl text-rose-900 mb-2 flex items-center gap-2" data-testid="profile-name">
                  {profile.first_name} {profile.last_name?.[0]}.
                  {profile.is_id_verified && (
                    <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-sm font-medium" title={t('common.verified')}>
                      ✓ {t('common.verified')}
                    </span>
                  )}
                  {age && <span className="text-rose-600 font-sans text-2xl">{age}</span>}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-rose-600">
                  {profile.country && (
                    <div className="flex items-center gap-1">
                      <span>→</span>
                      <span>{profile.city ? `${profile.city}, ` : ''}{profile.country}</span>
                    </div>
                  )}
                  {profile.muslim_type && (
                    <span className="bg-rose-100 text-rose-900 px-3 py-1 rounded-full text-sm font-medium">
                      {profile.muslim_type}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowIntentionModal(true)}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6"
                  data-testid="send-interest-btn"
                >
                  {t('profile.sendInterest')}
                </Button>
                <Button
                  onClick={handleInitiateChat}
                  variant="outline"
                  className="border-rose-500 text-rose-600 hover:bg-rose-50 rounded-full px-6"
                  data-testid="start-chat-btn"
                >
                  {t('profile.startChat')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="md:col-span-2 space-y-6">
            {/* About */}
            {profile.about_me && (
              <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-testid="about-section">
                <h2 className="font-serif text-xl text-rose-900 mb-4">{t('profile.aboutSection')}</h2>
                <p className="text-rose-600 leading-relaxed">{profile.about_me}</p>
              </div>
            )}

            {/* Looking For */}
            {profile.looking_for && (
              <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-testid="looking-for-section">
                <h2 className="font-serif text-xl text-rose-900 mb-4 flex items-center gap-2">
                  <span className="text-rose-500">→</span>
                  {t('profile.lookingForSection')}
                </h2>
                <p className="text-rose-600 leading-relaxed">{profile.looking_for}</p>
              </div>
            )}

            {/* Deal Breakers */}
            {profile.deal_breakers?.length > 0 && (
              <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-testid="dealbreakers-section">
                <h2 className="font-serif text-xl text-rose-900 mb-4 flex items-center gap-2">
                  <span className="text-rose-500">!</span>
                  {t('profile.dealBreakers')}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.deal_breakers.map((item, index) => (
                    <span key={index} className="bg-red-50 text-red-700 px-3 py-1 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Interests */}
            {profile.interests?.length > 0 && (
              <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-testid="interests-section">
                <h2 className="font-serif text-xl text-rose-900 mb-4">{t('profile.interestsHobbies')}</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((item, index) => (
                    <span key={index} className="bg-rose-100 text-rose-700 px-3 py-1 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Basic Details */}
            <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-testid="details-section">
              <h2 className="font-serif text-xl text-rose-900 mb-4">{t('profile.details')}</h2>
              <div className="space-y-4 text-sm">
                {profile.marital_status && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.maritalStatus')}</span>
                    <span className="text-rose-900 capitalize">{profile.marital_status.replace('_', ' ')}</span>
                  </div>
                )}
                {profile.height_cm && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.height')}</span>
                    <span className="text-rose-900">{profile.height_cm} cm</span>
                  </div>
                )}
                {profile.education && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.education')}</span>
                    <span className="text-rose-900">{profile.education}</span>
                  </div>
                )}
                {profile.profession && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.profession')}</span>
                    <span className="text-rose-900">{profile.profession}</span>
                  </div>
                )}
                {profile.ethnicity && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.ethnicity')}</span>
                    <span className="text-rose-900">{profile.ethnicity}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-rose-500">{t('profile.hasChildren')}</span>
                  <span className="text-rose-900">{profile.has_children ? t('common.yes') : t('common.no')}</span>
                </div>
                {profile.wants_children && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.wantsChildren')}</span>
                    <span className="text-rose-900 capitalize">{profile.wants_children}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Religious Details */}
            <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-testid="religious-section">
              <h2 className="font-serif text-xl text-rose-900 mb-4">{t('profile.religiousBackground')}</h2>
              <div className="space-y-4 text-sm">
                {profile.religiosity_level && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.religiosity')}</span>
                    <span className="text-rose-900">{profile.religiosity_level}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-rose-500">{t('profile.prayerFrequency')}</span>
                  <span className="text-rose-900">{profile.prays_regularly ? t('common.yes') : t('common.no')}</span>
                </div>
                {profile.hijab_preference && (
                  <div className="flex justify-between">
                    <span className="text-rose-500">{t('profile.hijab')}</span>
                    <span className="text-rose-900 capitalize">{profile.hijab_preference.replace('_', ' ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Languages */}
            {profile.languages?.length > 0 && (
              <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6" data-testid="languages-section">
                <h2 className="font-serif text-xl text-rose-900 mb-4 flex items-center gap-2">
                  <span className="text-rose-500">🌐</span>
                  {t('profile.languages')}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang, index) => (
                    <span key={index} className="bg-emerald-100 text-rose-500 px-3 py-1 rounded-full text-sm">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Intention Modal */}
      <IntentionModal
        isOpen={showIntentionModal}
        onClose={() => setShowIntentionModal(false)}
        onConfirm={handleSendInterest}
        targetName={profile?.first_name || "this person"}
      />
    </div>
  );
};

export default ProfileView;
